import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "styled-components";
import { Button, Input, Popup, SearchBar } from "antd-mobile";
import useNavigation from "../../hooks/useNavigation";
import PageWrap from "../components/PageWrap";
import PageHeader, { headerHeight } from "../components/PageHeader";
import { useStores } from "../../models";
import { ToastError, ToastSuccess } from "../../components/Toast";
import { EPayType, payTypeImage, payTypeLabel } from "../../components/Wallet";

const ScrollArea = styled.div`
  height: calc(100% - ${headerHeight});
  overflow: auto;
  background: #f7f8ff;
  padding: 12px;
`;

const Field = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
`;

const Label = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
`;

const TypeRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid #eee;
  cursor: pointer;
`;

const SettingPayMode = () => {
  const { goBack, navigate } = useNavigation();
  const { apiStore, authStore, walletStore, bankStore } = useStores();
  const [typePicker, setTypePicker] = useState(true);
  const [payType, setPayType] = useState<EPayType | null>(null);
  const [payAccount, setPayAccount] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [bankSearch, setBankSearch] = useState("");
  const [bankPicker, setBankPicker] = useState(false);
  const [payImage, setPayImage] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const realName = walletStore.realName || "";

  useEffect(() => {
    bankStore.getBankList?.();
    walletStore.getWalletInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bankOptions = useMemo(() => {
    const list = bankStore.bankList;
    const entries: [string, string][] = list?.entries
      ? Array.from(list.entries())
      : Object.entries(list || {});
    return entries
      .map(([key, value]) => ({ key, value: String(value) }))
      .filter((b) => !bankSearch || b.value.includes(bankSearch));
  }, [bankStore.bankList, bankSearch]);

  const pickImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      setPayImage(file);
      setPreview(URL.createObjectURL(file));
    };
    input.click();
  };

  const submit = async () => {
    if (!realName) {
      ToastError("请先完成实名认证");
      navigate("Verified");
      return;
    }
    if (!payAccount) return ToastError("请输入账号");
    if (payType === EPayType.BANK && !bankName) return ToastError("请选择银行");
    if (
      (payType === EPayType.ALIPAY || payType === EPayType.WECHAT) &&
      !payImage
    ) {
      return ToastError("请上传收款码");
    }

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("pay_account", payAccount);
      fd.append("real_name", realName);
      fd.append("pay_type", String(payType));
      if (payType === EPayType.BANK) {
        fd.append("bank_name", bankName);
        fd.append("bank_branch", bankBranch || "");
      } else if (payImage) {
        fd.append("file", payImage);
      }
      const res = await apiStore.createPay(fd as any, authStore.getToken);
      if (res?.code === 200 || res?.code === 0) {
        ToastSuccess("绑定成功");
        await walletStore.loadPaymentList();
        goBack(-1);
      } else {
        ToastError((res as any)?.message || "绑定失败");
      }
    } catch (e: any) {
      ToastError(e.message || "操作失败");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap>
      <PageHeader title="添加收款方式" />
      <ScrollArea>
        {payType != null && (
          <>
            <Field>
              <Label>收款类型</Label>
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src={payTypeImage(payType)} alt="" width={22} height={22} />
                <span style={{ marginLeft: 8 }}>{payTypeLabel(payType)}</span>
                <Button
                  size="mini"
                  fill="none"
                  color="primary"
                  style={{ marginLeft: "auto" }}
                  onClick={() => setTypePicker(true)}
                >
                  更换
                </Button>
              </div>
            </Field>
            <Field>
              <Label>真实姓名</Label>
              <Input value={realName} disabled />
            </Field>
            <Field>
              <Label>
                {payType === EPayType.BANK
                  ? "银行卡号"
                  : payType === EPayType.ALIPAY
                  ? "支付宝账号"
                  : "微信账号"}
              </Label>
              <Input
                value={payAccount}
                onChange={setPayAccount}
                placeholder="请输入账号"
              />
            </Field>
            {payType === EPayType.BANK ? (
              <>
                <Field onClick={() => setBankPicker(true)} style={{ cursor: "pointer" }}>
                  <Label>开户银行</Label>
                  <div>{bankName || "请选择银行 ›"}</div>
                </Field>
                <Field>
                  <Label>开户支行（选填）</Label>
                  <Input value={bankBranch} onChange={setBankBranch} placeholder="支行名称" />
                </Field>
              </>
            ) : (
              <Field onClick={pickImage} style={{ cursor: "pointer" }}>
                <Label>收款码</Label>
                {preview ? (
                  <img src={preview} alt="" width={120} height={120} style={{ objectFit: "cover" }} />
                ) : (
                  <div style={{ color: "#999" }}>点击上传收款码</div>
                )}
              </Field>
            )}
            <Button color="primary" block loading={loading} onClick={submit}>
              提交绑定
            </Button>
          </>
        )}
      </ScrollArea>

      <Popup
        visible={typePicker}
        onMaskClick={() => {
          if (payType == null) goBack(-1);
          else setTypePicker(false);
        }}
        bodyStyle={{ borderTopLeftRadius: 16, borderTopRightRadius: 16, paddingBottom: 28 }}
      >
        <div style={{ textAlign: "center", fontWeight: 700, padding: 16 }}>选择收款方式</div>
        {[EPayType.BANK, EPayType.ALIPAY, EPayType.WECHAT].map((t) => (
          <TypeRow
            key={t}
            onClick={() => {
              setPayType(t);
              setTypePicker(false);
            }}
          >
            <img src={payTypeImage(t)} alt="" width={22} height={22} />
            <span style={{ marginLeft: 10, flex: 1 }}>{payTypeLabel(t)}</span>
            <span>›</span>
          </TypeRow>
        ))}
      </Popup>

      <Popup
        visible={bankPicker}
        onMaskClick={() => setBankPicker(false)}
        bodyStyle={{ height: "70vh", borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
      >
        <div style={{ padding: 12 }}>
          <SearchBar
            placeholder="搜索银行"
            value={bankSearch}
            onChange={setBankSearch}
          />
        </div>
        <div style={{ height: "calc(70vh - 60px)", overflow: "auto" }}>
          {bankOptions.map((b) => (
            <TypeRow
              key={b.key}
              onClick={() => {
                setBankName(b.key);
                setBankPicker(false);
              }}
            >
              {b.value || b.key}
            </TypeRow>
          ))}
        </div>
      </Popup>
    </PageWrap>
  );
};

export default observer(SettingPayMode);
